// Public, unauthenticated document views (legacy components/views/*.vue):
//   /views/{sale|purchase|quotation|return_order|payment}/:hash
// Data comes from GET /admin/views/{type}/{hash} (OrderViewController, no auth).

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import { moduleRootUrl } from '../../api/client';
import { t } from '../../i18n';
import { OrderDocument, type OrderDocumentKind } from './OrderDocument';
import { PaymentDocument } from './PaymentDocument';
import { PrintableDocument } from './PrintableDocument';
import { PublicPaymentForms } from './PublicPaymentForms';
import type { AnyRecord } from './format';
import { UI } from '../../brand/ui';

const VIEW_TYPES = ['sale', 'purchase', 'quotation', 'return_order', 'payment'] as const;
type ViewType = (typeof VIEW_TYPES)[number];

export async function fetchPublicDocument(type: ViewType, hash: string): Promise<AnyRecord> {
  const response = await fetch(`${moduleRootUrl()}/views/${type}/${encodeURIComponent(hash)}`, {
    credentials: 'include',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
  });
  if (!response.ok) throw new Error(String(response.status));
  return (await response.json()) as AnyRecord;
}

export function PublicDocumentPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const type = (VIEW_TYPES as readonly string[]).includes(params.type ?? '') ? (params.type as ViewType) : null;
  const hash = params.hash ?? '';
  const [record, setRecord] = useState<AnyRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!type || !hash) {
      setError(t('doc.notFoundText'));
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    fetchPublicDocument(type, hash)
      .then((data) => {
        if (!active) return;
        setRecord(data);
        document.title = `${t(`doc.kind.${type}`)} ${String(data.reference ?? '')}`;
      })
      .catch(() => active && setError(t('doc.notFoundText')))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [type, hash]);

  const queryError = searchParams.get('error');
  const message = searchParams.get('message');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: UI.surface, py: { xs: 2, md: 5 }, px: { xs: 1, md: 2 } }}>
      <Stack spacing={2} sx={{ maxWidth: 860, mx: 'auto' }}>
        {queryError ? <Alert severity="error">{queryError}</Alert> : null}
        {message ? <Alert severity="info">{message}</Alert> : null}
        {loading ? (
          <Stack alignItems="center" sx={{ py: 10 }}>
            <CircularProgress />
          </Stack>
        ) : error || !record || !type ? (
          <Paper sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('doc.notFound')}
            </Typography>
            <Typography color="text.secondary">{error ?? t('doc.notFoundText')}</Typography>
          </Paper>
        ) : (
          <>
            <Paper sx={{ p: { xs: 2, md: 4 } }}>
              <PrintableDocument title={`${t(`doc.kind.${type}`)} ${String(record.reference ?? '')}`}>
                {type === 'payment' ? <PaymentDocument record={record} /> : <OrderDocument record={record} kind={type as OrderDocumentKind} />}
              </PrintableDocument>
            </Paper>
            {type === 'payment' ? <PublicPaymentForms payment={record} onPaid={setRecord} /> : null}
          </>
        )}
      </Stack>
    </Box>
  );
}
